import type { JSX, KeyboardEventHandler, ReactNode } from 'react';

import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogHelpLink,
  DialogTitle,
} from './dialog.js';

/**
 * AppDialog — typed Dialog wrapper that makes the Radix a11y contract
 * impossible to forget. Mirror of AppSheet for the Dialog primitive.
 *
 * The contract: every Dialog must have a Title that the screen reader
 * uses for `aria-labelledby`. Raw shadcn Dialog leaves that to per-call
 * discipline; this wrapper requires it at compile time, so a future "I
 * added a dialog and forgot the title" regression is a TS error, not a
 * quiet a11y violation.
 *
 * `titleHidden` is the right choice when the dialog already renders a
 * custom-styled visible header — Radix VisuallyHidden moves the
 * mandatory DialogTitle off-screen while keeping it in the
 * accessibility tree. The visible header stays untouched.
 */
export interface AppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Mandatory — sets aria-labelledby so screen readers announce the dialog. */
  title: string;
  /** Optional — adds aria-describedby for additional context. */
  description?: string;
  /**
   * Optional — a short muted note on the title's own line, such as a file's size. It sits beside the
   * title rather than in it, so the dialog's accessible name stays the title alone.
   */
  titleMeta?: string;
  /**
   * Optional — controls placed at the right end of the title line. The file viewer docks its toolbar here
   * in full screen. The question mark and the close button then join the same line after them, in one
   * row and centred with them, instead of floating in the corner over the controls.
   */
  titleAction?: ReactNode;
  /**
   * Visually hide the title (still accessible to screen readers) for dialogs
   * that have a custom header design or no header at all. Use when the
   * dialog already renders a visible h1/h2/p as its title.
   */
  titleHidden?: boolean;
  className?: string;
  showCloseButton?: boolean;
  /**
   * Optional deep link into the employee guide, rendered as a question mark beside the close
   * button. Point it at the section that explains this screen — see `lib/guide-links.ts`.
   */
  helpHref?: string;
  helpLabel?: string;
  /** Optional inline style — used for sizing dialogs that need explicit dims. */
  style?: React.CSSProperties;
  /**
   * Optional id passthrough for aria-labelledby anchoring from inside content. Left out, Radix names
   * the dialog after its title, so the prop is only set when given.
   */
  'aria-labelledby'?: string;
  /** Optional keyboard handler — needed for dialogs with Cmd+Enter submit shortcuts. */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /**
   * Radix focus management. Defaults to `(e) => e.preventDefault()` so the
   * dialog opens without yanking visible focus to the first tabbable child
   * (close button / first input) — that focus jump was the most common
   * complaint about modal UX in the Teams iframe. Pass a custom handler
   * (or a no-op) to opt back into Radix's auto-focus behavior on a
   * per-dialog basis when you actually want an input pre-focused.
   */
  onOpenAutoFocus?: (event: Event) => void;
  /** Same idea on close — useful if the trigger element should not regain focus. */
  onCloseAutoFocus?: (event: Event) => void;
  /** Radix Escape hook — call `event.preventDefault()` to keep the dialog open, e.g. to leave a full-screen mode first. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  children: ReactNode;
}

/** The question mark and the close button on the title line: the height of the toolbar's buttons, centred with them. */
const INLINE_CONTROL_CLASS =
  'inline-flex size-8 shrink-0 items-center justify-center';

const preventAutoFocus = (event: Event): void => event.preventDefault();

export function AppDialog({
  open,
  onOpenChange,
  title,
  description,
  titleMeta,
  titleAction,
  titleHidden = false,
  className,
  showCloseButton = true,
  helpHref,
  helpLabel,
  style,
  'aria-labelledby': ariaLabelledBy,
  onKeyDown,
  onOpenAutoFocus = preventAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  children,
}: AppDialogProps): JSX.Element {
  const controlsInline = titleAction !== undefined;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={className}
        showCloseButton={showCloseButton && !controlsInline}
        helpHref={controlsInline ? undefined : helpHref}
        helpLabel={helpLabel}
        style={style}
        {...(ariaLabelledBy === undefined
          ? {}
          : { 'aria-labelledby': ariaLabelledBy })}
        onKeyDown={onKeyDown}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        onEscapeKeyDown={onEscapeKeyDown}
      >
        {titleMeta !== undefined || titleAction !== undefined ? (
          <div
            data-testid="dialog-title-row"
            className={
              controlsInline
                ? 'flex min-w-0 items-center gap-2'
                : 'flex min-w-0 items-center gap-2 pr-6'
            }
          >
            <DialogTitle
              className={titleHidden ? 'sr-only' : 'min-w-0 truncate'}
            >
              {title}
            </DialogTitle>
            {titleMeta !== undefined && titleMeta.length > 0 && (
              <span className="shrink-0 text-sm text-muted-foreground">
                {titleMeta}
              </span>
            )}
            {controlsInline && (
              <div className="ml-auto flex min-w-0 items-center">
                {titleAction}
              </div>
            )}
            {controlsInline &&
              helpHref !== undefined &&
              helpHref.length > 0 && (
                <DialogHelpLink
                  href={helpHref}
                  {...(helpLabel === undefined ? {} : { label: helpLabel })}
                  className={INLINE_CONTROL_CLASS}
                />
              )}
            {controlsInline && showCloseButton && (
              <DialogCloseButton className={INLINE_CONTROL_CLASS} />
            )}
          </div>
        ) : (
          <DialogTitle className={titleHidden ? 'sr-only' : undefined}>
            {title}
          </DialogTitle>
        )}
        {description !== undefined && description.length > 0 && (
          <DialogDescription className={titleHidden ? 'sr-only' : undefined}>
            {description}
          </DialogDescription>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}
